import PropTypes from 'prop-types';
import React, { useEffect, useRef, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { useRecoilState, useRecoilValue, useSetRecoilState } from 'recoil';
import { v4 } from 'uuid';
import { CanBackToResultState, DataExtendState, PlaceRelatedState, UserState } from '../../appState';
import { CommonConfig, LinkIconConfig } from '../../config';
import { RouterConfig } from '../../config/routerConfig';
import { UrlConfig } from '../../config/urlConfig';
import { AppData } from '../../data';
import { CodeEnum, GeometryTypeEnum, MapTypeEnum, PlaceExtensionTypeEnum, TypeRequestUpdateEnum } from '../../enum';
import { Resource } from '../../resource';
import { ApiTool, AppTool, MapTool, PlaceTool, SidebarTool, StringTool, UrlTool, UserTool } from '../../tool';
import DescriptionDisplay from '../common/descriptionDisplay/descriptionDisplay';
import DirectionButton from '../common/directionButton/directionButton';
import DisplayBusinessHour from '../common/displayBusinessHour/displayBusinessHour';
import DisplayLink from '../common/displayLink/displayLink';
import DisplayMedia from '../common/displayMedia/displayMedia';
import DisplayVr360 from '../common/displayVr360/displayVr360';
import Error from '../common/error/error';
import RequestUpdateBtn from '../common/requestUpdateBtn/requestUpdateBtn';
import SaveButton from '../common/saveButton/saveButton';
import ShareButton from '../common/shareButton/shareButton';
import SwipeableViewsImages from '../common/swipeableViewsImages/swipeableViewsImages';
import ListItemLink from '../listItemLink/listItemLink';
import ListPlaceRelated from '../listPlaceRelated/listPlaceRelated';
import PlaceRelatedV2 from '../placeRelatedV2/placeRelatedV2';
import { MapTypeAppState } from './../../appState/mapTypeAppState';
import { GeoJsonTool } from './../../tool/geoJsonTool';
import DisplayGroup from './../common/displayGroup/displayGroup';
import './poiDetail.scss';
import TooltipV2 from '../tooltipV2/tooltipV2';

const worldCoords = [[-180, -90], [-180, 90], [180, 90], [180, -90], [-180, -90]]

const PADDING_DEFAULT = 420

function PoiDetail(props) {
  const { id, text, type, lat, lng, data, closeEnable = true, onClose, hiddenDirection, disableTextSearch } = props
  const userState = useRecoilValue(UserState)
  const history = useHistory()
  const location = useLocation()
  const [place, setPlace] = useState(null)
  const polygonRef = useRef(null)
  const polylineRef = useRef(null)
  const polygonsRef = useRef([])
  const polylinesRef = useRef([])

  const setMapTypeAppState = useSetRecoilState(MapTypeAppState)
  const [dataExtendState, setDataExtendState] = useRecoilState(DataExtendState)
  const [placeRelatedState, setPlaceRelatedState] = useRecoilState(PlaceRelatedState)
  const setCanBackToResultState = useSetRecoilState(CanBackToResultState)

  const polygonHoleRef = useRef(null)

  const currentPlace = useRef(null)
  const [keySaved, setKeySaved] = useState(() => {
    return v4()
  })

  const [loading, setLoading] = useState(false)
  const [links, setLinks] = useState([])
  const [vr360, setVr360] = useState([])

  useEffect(() => {
    if ((type || text) && id) {
      setCanBackToResultState({
        show: true,
        text: text,
        type: type
      })
    }
    return () => {
      setCanBackToResultState(null)
    }
  }, [type, text, id])

  useEffect(() => {
    if (!disableTextSearch) {
      AppTool.setSearchBarText(place?.name || "")
    }
  }, [place?.name])

  useEffect(() => {
    var sourcePlaces

    // If we have data passed from parent, use it directly
    if (data) {
      console.log('=== USING PASSED DATA ===');
      console.log('Passed data:', data);
      console.log('Passed data name:', data?.name);

      // Transform the passed data to match the expected structure
      let transformedPlace = {
        place_id: data?.id,
        name: data?.name,
        formatted_address: data?.address,
        types: data?.types,
        geometry: {
          location: data?.location
        },
        // Copy other properties that might be available
        ...data
      };

      setMarkerAndShowInfo(transformedPlace)
      setPlace(transformedPlace)
      if (transformedPlace?.geometry?.location) {
        moveCamera(transformedPlace)
      }
    }
    // If no data passed, make API call as before
    else if (id) {
      AppTool.setSearchBarLoading(true)
      SidebarTool.setOpen(true)
      setLoading(true)
      sourcePlaces = ApiTool.get(`${UrlConfig.poi.detail}?place_id=${id}`, (res) => {
        setLoading(false)
        AppTool.setSearchBarLoading(false)
        if (res?.status == CodeEnum.OK) {
          let typeCheck = PlaceTool.isAreaOrStreet(res?.result)
          setMarkerAndShowInfo(res?.result)
          drawPoly(res?.result)
          setPlace(res?.result)
          // res?.result?.object?.id && AppData.map.setSelectedBuildings([res?.result?.object?.id])
          if (!typeCheck && !location.state?.disableMoveMap) {
            moveCamera(res?.result)
          }
          let classifyMetaData = PlaceTool.classifyMetaData(res?.result?.metadata)
          let placeExtend = PlaceTool.convertClassifyMetaDataToPlaceExTend(classifyMetaData)
          let links = placeExtend?.link.sort((a, b) => a.order - b.order)
          let vr360 = placeExtend?.vr360.sort((a, b) => a.order - b.order)
          setLinks(links)
          setVr360(vr360)
        }
        else {
          setPlace(null)
        }
      })
    }

    return () => {
      currentPlace.current?.setMap(null)
      polylineRef.current?.setMap(null)
      polygonRef.current?.setMap(null)
      polygonsRef.current?.forEach(polygon => {
        polygon?.setMap(null)
      })
      polylinesRef.current?.forEach(polyline => {
        polyline?.setMap(null)
      })
      polygonHoleRef.current?.setMap(null)
      sourcePlaces?.cancel()
      // AppData.map.setSelectedBuildings([])
    }
  }, [id, data])

  useEffect(() => {
    let api
    if (id && userState?.id) {
      let body = {
        placeId: id
      }
      api = ApiTool.post(UrlConfig.searchHistory.save, body, (res) => {
      }, true)
    }
    return () => {
      api?.cancel()
    }
  }, [[id, userState?.id]])

  useEffect(() => {
    var sourceLocation
    if (StringTool.isNumeric(lat) && StringTool.isNumeric(lng)) {
      let numLat = parseFloat(lat)
      let numLng = parseFloat(lng)
      if (-90 <= numLat && numLat <= 90 && -180 <= numLng && numLng <= 180) {
        let placeTemp = {
          location: {
            lat: numLat,
            lng: numLng
          },
          name: `${numLat.toFixed(6)}, ${numLng.toFixed(6)}`
        }
        let body = {
          latlng: `${numLat},${numLng}`,
        }
        setMarkerAndShowInfo(placeTemp)
        moveCamera(placeTemp)
        setPlace(placeTemp)
        AppTool.setSearchBarLoading(true)
        SidebarTool.setOpen(true)
        setLoading(true)
        sourceLocation = ApiTool.queryGetFromJson(UrlConfig.geocode.geocode, body, (res) => {
          setLoading(false)
          AppTool.setSearchBarLoading(false)
          if (res?.status == CodeEnum.OK) {
            let newPlace = {
              ...placeTemp,
              address: res?.result.formatted_address
            }
            setPlace(newPlace)
          }
          else {
            setPlace(null)
          }
        })

      }
    }
    return () => {
      sourceLocation?.cancel()
    }
  }, [lat, lng])

  useEffect(() => {
    setDataExtendState({
      show: false,
      dataExtend: [],
      indexSelect: 0,
      typeData: PlaceExtensionTypeEnum.none
    })
  }, [id])

  useEffect(() => {
    let placeCustom = null
    try {
      placeCustom = JSON.parse(decodeURIComponent(data))
    } catch (error) {

    }
    setMarkerAndShowInfo(placeCustom)
    moveCamera(placeCustom)
    setPlace(placeCustom)
    SidebarTool.setOpen(true)
  }, [data])

  const setMarkerAndShowInfo = (place) => {
    if (place) {
      if (place?.geometry?.location) {
        currentPlace.current = new ngm.map.Marker({
          position: place?.geometry?.location,
          icon: { size: { height: 36, width: 36 }, url: LinkIconConfig.placeDetail.marker },
          title: place?.name,
          zIndex: 99999
        })
        currentPlace?.current.setMap(AppData.map)
      }
    }
  }

  const moveCamera = (place) => {
    if (place && place?.geometry?.location) {
      let padding = {
        top: 10,
        left: PADDING_DEFAULT * 2,
        right: 10,
        bottom: 10
      }
      MapTool.moveWithPadding(AppData.map, place?.geometry?.location, padding, { animate: true })
    }
  }

  const drawPolyline = (data) => {
    polylineRef.current = new ngm.map.Polyline({
      path: data || [],
      strokeColor: "#3335DF",
      strokeOpacity: 1.0,
      strokeWidth: 5,
      userInteractionEnabled: false,
    })
    polylineRef.current.setMap(AppData.map)
  }

  const drawPolygon = (data) => {
    polygonHoleRef.current = new ngm.map.Polygon({
      paths: [worldCoords, data[0]],
      fillColor: "#000000",
      fillOpacity: 0.2,
      strokeWidth: 1,
      userInteractionEnabled: false,
    })
    polygonHoleRef.current.setMap(AppData.map)
  }

  const drawMultiPolyline = (data) => {
    if (data && data.length > 0) {
      polylinesRef.current = []
      for (let i = 0; i < data.length; i++) {
        let polyline = new ngm.map.Polyline({
          path: data[i],
          strokeColor: CommonConfig.strokeBlue,
          strokeOpacity: 1,
          strokeWidth: 7,
          userInteractionEnabled: false,
        })
        polyline.setMap(AppData.map)
        polylinesRef.current.push(polyline)
      }
    }
  }

  const drawMultiPolygon = (data) => {
    if (data && data.length > 0) {
      let holePaths = []
      for (let i = 0; i < data.length; i++) {
        holePaths.push(data[i][0])
      }
      polygonHoleRef.current = new ngm.map.Polygon({
        paths: [worldCoords, ...holePaths],
        fillColor: "#000000",
        fillOpacity: 0.2,
        strokeWidth: 1,
        userInteractionEnabled: false,
      })
      polygonHoleRef.current.setMap(AppData.map)
    }
  }

  const drawPoly = (place) => {
    if (place) {
      if (place?.geometry?.type) {
        let geometry = place?.geometry
        switch (geometry.type) {
          case GeometryTypeEnum.lineString:
            drawPolyline(geometry.coordinates)
            break
          case GeometryTypeEnum.polygon:
            drawPolygon(geometry.coordinates || [])
            break
          case GeometryTypeEnum.multiLineString:
            drawMultiPolyline(geometry.coordinates || [])
            break
          case GeometryTypeEnum.multiPolygon:
            drawMultiPolygon(geometry.coordinates || [])
            break
        }
        let bound = new ngm.map.LatLngBounds()
        let points = GeoJsonTool.getPointForBound(geometry.coordinates, geometry.type)
        if (points?.length > 0) {
          for (let i = 0; i < points.length; i++) {
            bound.extend(points[i])
          }
          let paddingOptions = {
            top: 10,
            bottom: 10,
            left: CommonConfig.widthSearchBar + (CommonConfig.padding * 2) + 10,
            right: 10
          }
          let camera = AppData.map.getCameraWithBounds(bound, paddingOptions)
          if (camera.getZoom() > 19) {
            camera.setZoom(19)
          }
          AppTool.moveCamera(camera, { animate: true })
        }
      }
    }
  }

  const handleBackToResult = () => {
    history.push({
      pathname: RouterConfig.place.search.replace("/:type?", type ? "/" + type : ""),
      search: UrlTool.createSearch({ text: text })
    })
  }

  const onChangeCountSaveGroup = () => {
    setKeySaved(v4())
  }

  const handleOnClickWebsite = (link) => (e) => {
    window.open(link, '_blank')
  }

  const handleOnBack = (item) => (e) => {
    setDataExtendState({
      show: false, dataExtend: [],
      indexSelect: 0,
      typeData: PlaceExtensionTypeEnum.none
    })
  }

  const user = UserTool.getUser()

  let urlWebSite = place?.website || ""
  if (!/^(http|https):/.test(urlWebSite)) {
    urlWebSite = 'http://' + urlWebSite
  }

  const onCloseDetail = () => {
    onClose && onClose()
  }

  let urlShare = window.location.origin + RouterConfig.place.detail.replace(":id", place?.place_id)

  return (
    <div className='poiDetailCpn'>
      <div className='boxContainer' key={place?.place_id || v4()}>
        <div className='boxInfo'>
          {
            !loading &&
            (
              !place ?
                <div className='boxDataNotFound'>
                  <Error message={Resource.message.dataNotFound} subMessage={Resource.message.dataDoesNotExist} />
                </div>
                :
                <>
                  {/* Header Image */}
                  <div className='detailHeader'>
                    <SwipeableViewsImages disableSwitch place={place} paddingImages={0} sizeButton={30} />
                    {closeEnable && (
                      <button className='closeButton' onClick={onCloseDetail}>
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                          <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" />
                        </svg>
                      </button>
                    )}
                  </div>

                  {/* Main Info Section */}
                  <div className='detailContent'>
                    <div className='mainInfo'>
                      <h1 className='placeName'>{place?.name || 'Unnamed Place'}</h1>
                      {place?.types?.length > 0 && (
                        <div className='placeSubtitle'>
                          {place?.types?.find(type => type !== 'establishment' && type !== 'point_of_interest') || place?.types[0] || ''}
                        </div>
                      )}

                      {/* Rating Section */}
                      {place?.rating && (
                        <div className='ratingSection'>
                          <div className='ratingRow'>
                            <span className='ratingValue'>{place.rating}</span>
                            <div className='starsContainer'>
                              {[1, 2, 3, 4, 5].map(star => (
                                <svg key={star} width="16" height="16" viewBox="0 0 24 24" fill={star <= Math.floor(place.rating) ? '#fbbc04' : '#e8eaed'}>
                                  <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
                                </svg>
                              ))}
                            </div>
                            <span className='reviewCount'>({place?.user_ratings_total || 0})</span>
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Action Buttons */}
                    <div className='actionButtons'>
                      {!hiddenDirection && (
                        <DirectionButton latlng={place?.geometry?.location} />
                      )}
                      {place?.place_id && (
                        <SaveButton id={place?.place_id} onChange={onChangeCountSaveGroup} />
                      )}
                      <ShareButton url={urlShare} />
                    </div>

                    {/* Information Section */}
                    <div className='infoSection'>
                      {place?.formatted_address && (
                        <div className='infoItem'>
                          <div className='infoIcon'>
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                              <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z" />
                            </svg>
                          </div>
                          <div className='infoText'>
                            <span>{place.formatted_address}</span>
                          </div>
                        </div>
                      )}

                      {place?.businessHours?.length > 0 && (
                        <div className='infoItem'>
                          <div className='infoIcon'>
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                              <path d="M11.99 2C6.47 2 2 6.48 2 12s4.47 10 9.99 10C17.52 22 22 17.52 22 12S17.52 2 11.99 2zM12 20c-4.42 0-8-3.58-8-8s3.58-8 8-8 8 3.58 8 8-3.58 8-8 8z" />
                              <path d="M12.5 7H11v6l5.25 3.15.75-1.23-4.5-2.67z" />
                            </svg>
                          </div>
                          <div className='infoText'>
                            <DisplayBusinessHour businessHours={place.businessHours} />
                          </div>
                        </div>
                      )}

                      {place?.phoneNumber && (
                        <div className='infoItem'>
                          <div className='infoIcon'>
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                              <path d="M6.62 10.79c1.44 2.83 3.76 5.14 6.59 6.59l2.2-2.2c.27-.27.67-.36 1.02-.24 1.12.37 2.33.57 3.57.57.55 0 1 .45 1 1V20c0 .55-.45 1-1 1-9.39 0-17-7.61-17-17 0-.55.45-1 1-1h3.5c.55 0 1 .45 1 1 0 1.25.2 2.45.57 3.57.11.35.03.74-.25 1.02l-2.2 2.2z" />
                            </svg>
                          </div>
                          <div className='infoText'>
                            <span>{place.phoneNumber}</span>
                          </div>
                        </div>
                      )}

                      {place?.website && (
                        <div className='infoItem'>
                          <div className='infoIcon'>
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                              <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-1 17.93c-3.94-.49-7-3.85-7-7.93 0-.62.08-1.21.21-1.79L9 15v1c0 1.1.9 2 2 2v1.93zm6.9-2.54c-.26-.81-1-1.39-1.9-1.39h-1v-3c0-.55-.45-1-1-1H8v-2h2c.55 0 1-.45 1-1V7h2c1.1 0 2-.9 2-2v-.41c2.93 1.19 5 4.06 5 7.41 0 2.08-.8 3.97-2.1 5.39z" />
                            </svg>
                          </div>
                          <div className='infoText'>
                            <a href={urlWebSite} target='_blank' rel='noreferrer'>{place.website}</a>
                          </div>
                        </div>
                      )}
                    </div>

                    <DisplayGroup id={place?.place_id} placeName={place?.name} key={keySaved} />
                  </div>
                </>
            )
          }
        </div>
        {
          closeEnable &&
          <TooltipV2 description={Resource.common.close}>
            <div className='closeIcon' onClick={onCloseDetail}>
              <img src={LinkIconConfig.common.closeBlack} alt="close" />
            </div>
          </TooltipV2>
        }
      </div>

      {
        dataExtendState.typeData == PlaceExtensionTypeEnum.link &&
        <ListItemLink />
      }

      {
        placeRelatedState.show &&
        <ListPlaceRelated />
      }
    </div>
  )
}

PoiDetail.propTypes = {
  id: PropTypes.string,
  text: PropTypes.string,
  type: PropTypes.string,
  lat: PropTypes.number,
  lng: PropTypes.number,
  data: PropTypes.any,
  closeEnable: PropTypes.bool,
  onClose: PropTypes.func,
  hiddenDirection: PropTypes.bool,
  disableTextSearch: PropTypes.bool
};

export default PoiDetail;
